import { faHeart as faHeartEmpty } from "@fortawesome/free-regular-svg-icons";
import {
    faBriefcase,
    faCalendarDays,
    faClock,
    faCoins,
    faCopy,
    faHandFist,
    faHeart as faHeartFull,
    faIdCard,
    faLightbulb,
    faLocationDot,
    faVideo,
    faWrench,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format } from "date-fns";
import { forwardRef, memo, useEffect, useState } from "react";
import { CertificateIcon } from "../assets/icons";
import { ASSETS } from "../config/assets";
import { useFavourites } from "../hooks/useFavourites";
import "./JobItem.css";
import { Stars } from "./Stars";

export const SPECIALIZATION_ICON_MAP = {
    Stagehands: faHandFist,
    Osvětlovač: faLightbulb,
    Zvukař: "speaker",
    "AV technik": faVideo,
    Rigger: faWrench,
    stagehands: faHandFist,
    light_technician: faLightbulb,
    sound_technician: "speaker",
    av_technician: faVideo,
    rigger: faWrench,
};

const LICENSE_LABEL_MAP = {
    B: "Skupina B",
    C: "Skupina C",
    D: "Skupina D",
    E: "Skupina E",
    none: "Bez požadavku",
    "": "Bez požadavku",
};

const DEFAULT_SPECIALIZATION_ICON = faBriefcase;

const SpeakerIconSVG = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="job_item_specialization_icon"
    >
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M17 2a3 3 0 0 1 3 3v14a3 3 0 0 1 -3 3h-10a3 3 0 0 1 -3 -3v-14a3 3 0 0 1 3 -3zm-5 9a4 4 0 0 0 -3.995 3.8l-.005 .2a4 4 0 1 0 4 -4m0 -5a1 1 0 0 0 -1 1v.01a1 1 0 0 0 2 0v-.01a1 1 0 0 0 -1 -1" />
    </svg>
);

const getSpecializationIcon = (specialization) => {
    const icon = SPECIALIZATION_ICON_MAP[specialization];

    if (icon === "speaker") {
        return <SpeakerIconSVG />;
    }

    return (
        <FontAwesomeIcon
            icon={icon || DEFAULT_SPECIALIZATION_ICON}
            className={`job_item_specialization_icon ${!icon ? "job_item_specialization_icon--default" : ""}`}
        />
    );
};

const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.slice(0, 10).split("-").map(Number);
    if (!year || !month || !day) return null;
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
        ? date
        : null;
};

const CZ_WEEKDAY_ABBR = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];

const getWeekdayAbbr = (date) => CZ_WEEKDAY_ABBR[date.getDay()];

const formatTime = (timeStr) =>
    timeStr ? timeStr.slice(0, 5) : "--:--";

const isMultiDay = (start, end) => {
    if (!start || !end) return false;
    return (
        end.getDate() !== start.getDate() ||
        end.getMonth() !== start.getMonth() ||
        end.getFullYear() !== start.getFullYear()
    );
};

const renderDateTime = (starts_at, starts_time, ends_at, ends_time) => {
    const startDate = parseLocalDate(starts_at);
    const endDate = parseLocalDate(ends_at) || startDate;
    const startTime = starts_time;
    const endTime = ends_time;

    if (!startDate) return { dateStr: "--", timeStr: "--" };

    const multiDay = isMultiDay(startDate, endDate);

    if (multiDay) {
        return {
            dateStr: `${format(startDate, "d. M.")} – ${format(endDate, "d. M. yyyy")}`,
            timeStr: `${getWeekdayAbbr(startDate)} ${formatTime(startTime)} – ${getWeekdayAbbr(endDate)} ${formatTime(endTime)}`,
        };
    }

    return {
        dateStr: `${getWeekdayAbbr(startDate)} ${format(startDate, "d. M.")}`,
        timeStr: `${formatTime(startTime)} – ${formatTime(endTime)}`,
    };
};

const JobItemBase = forwardRef((props, ref) => {
    const {
        id,
        title,
        starts_at,
        starts_time,
        ends_at,
        ends_time,
        city,
        location_name,
        organizerName,
        organizerImage,
        organizerRating,
        organizerVerified,
        specialization,
        payMin,
        payMax,
        payType,
        isFavouriteInitially,
        onClick,
        isOwner,
        activeItem,
        isSos,
        license,
        confirmedCount,
        requiredCount,
        positionId,
        isFavouritePositionInitially,
        onInterestClick,
        onCancelInterest,
        onShareClick,
        isPositionBooked,
        isUserApplied,
        applicants = [],
        onApplicationAction,
        onOpenChatWithApplicant,
    } = props;

    const { toggleFavourite } = useFavourites();
    const [isFav, setIsFav] = useState(isFavouriteInitially);
    const [isPosFav, setIsPosFav] = useState(isFavouritePositionInitially);
    const [applicantFavourites, setApplicantFavourites] = useState({});

    useEffect(() => {
        setIsFav(isFavouriteInitially);
        setIsPosFav(isFavouritePositionInitially);
    }, [isFavouriteInitially, isFavouritePositionInitially]);

    const handleFavClick = async (e) => {
        e.stopPropagation();
        const previousState = isFav;
        setIsFav(!previousState);
        try {
            await toggleFavourite("job", id);
        } catch {
            setIsFav(previousState);
        }
    };

    const handlePositionFavClick = async (e) => {
        e.stopPropagation();
        const previousState = isPosFav;
        setIsPosFav(!previousState);
        try {
            await toggleFavourite("position", positionId);
        } catch {
            setIsPosFav(previousState);
        }
    };

    const handleApplicantFavClick = async (userId) => {
        const previousState = !!applicantFavourites[userId];
        setApplicantFavourites((prev) => ({ ...prev, [userId]: !previousState }));
        try {
            await toggleFavourite("user", userId);
        } catch {
            setApplicantFavourites((prev) => ({ ...prev, [userId]: previousState }));
        }
    };

    const getApplicantFavState = (app) => {
        if (app.user_id in applicantFavourites) {
            return applicantFavourites[app.user_id];
        }
        return !!app.is_favourited_by_me;
    };

    const isDetailMode = license !== undefined || confirmedCount !== undefined || onInterestClick !== undefined;

    const isActive = activeItem !== false;
    const paySuffix = payType === "daily" ? "Kč / akci" : "Kč / hod.";
    const formatPay = (amount) =>
        Number(amount).toLocaleString("cs-CZ", {
            maximumFractionDigits: 2,
        });

    const { dateStr, timeStr } = renderDateTime(
        starts_at,
        starts_time,
        ends_at,
        ends_time,
    );

    return (
        <div
            className={`job_item ${!isActive ? "job_item-inactive" : ""}`}
            onClick={onClick}
            ref={ref}
        >
            <div className="job_item_content">
                <div className="job_item_icon_wrapper">
                    {getSpecializationIcon(specialization)}
                </div>

                <section className="job_item_work">
                    {!isActive && (
                        <div className="job_item_status_badge">Neaktivní nabídka</div>
                    )}

                    <div className="job_item_header">
                        {isSos && <span className="job_item_sos_badge">SOS</span>}
                        <h3 className="job_item_title">{title || "Bez názvu"}</h3>
                        {!isOwner && (
                            <FontAwesomeIcon
                                icon={isFav ? faHeartFull : faHeartEmpty}
                                className={`job_item_heart ${isFav ? "fav-active" : ""}`}
                                onClick={handleFavClick}
                            />
                        )}
                    </div>

                     <div className="job_item_details_grid">
                            <div className="job_item_detail_item">
                                <FontAwesomeIcon
                                    icon={faCalendarDays}
                                    className="job_item_detail_item_icon"
                                />
                                <span className="job_item_detail_value">{dateStr}</span>
                            </div>
                            <div className="job_item_detail_item">
                                <FontAwesomeIcon
                                    icon={faCoins}
                                    className="job_item_detail_item_icon"
                                />
                                <span className="job_item_detail_value highlight_pay">
                                    {payMin != null
                                        ? `${formatPay(payMin)}${payMax != null && payMax !== payMin ? `–${formatPay(payMax)}` : ""} ${paySuffix}`
                                        : "--"}
                                </span>
                            </div>
                            <div className="job_item_detail_item">
                                <FontAwesomeIcon
                                    icon={faClock}
                                    className="job_item_detail_item_icon"
                                />
                                <span className="job_item_detail_value">{timeStr}</span>
                            </div>
                            {isDetailMode ? (
                                license !== undefined && (
                                    <div className="job_item_detail_item job_item_detail_license">
                                        <FontAwesomeIcon
                                            icon={faIdCard}
                                            className="job_item_detail_item_icon license"
                                        />
                                        <span className="job_item_detail_value">
                                            {LICENSE_LABEL_MAP[license || "none"] || license || "Bez požadavku"}
                                        </span>
                                    </div>
                                )
                            ) : (
                                <div className={`job_item_detail_item job_item_detail_location ${city || location_name ? "" : "no-data"}`}>
                                    <FontAwesomeIcon
                                        icon={faLocationDot}
                                        className="job_item_detail_item_icon location"
                                    />
                                    {city ? (
                                        <>
                                            <span className="job_item_area">{city}</span>
                                            {location_name && (
                                                <>
                                                    <span className="job_item_sep"> · </span>
                                                    <span className="job_item_place">{location_name}</span>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <span className="job_item_detail_value text-muted">--</span>
                                    )}
                                </div>
                            )}
                            {isDetailMode && confirmedCount !== undefined && requiredCount !== undefined && (
                                <div className="job_item_detail_item job_item_detail_occupancy">
                                    <FontAwesomeIcon
                                        icon={faHeartFull}
                                        className="job_item_detail_item_icon"
                                    />
                                    <span className="job_item_detail_value">
                                        Obsazeno: {confirmedCount} / {requiredCount}
                                    </span>
                                </div>
                            )}
                        </div>
                </section>

                {isDetailMode ? (
                    <aside className="job_item_actions_panel">
                        {!isOwner && (
                            <>
                                {isUserApplied ? (
                                    <>
                                        <button
                                            className="job_item_action_btn applied"
                                            onClick={onCancelInterest}
                                        >
                                            Zrušit přihlášku
                                        </button>
                                        <span className="job_item_applied_status">
                                            Přihlášeno (čeká na potvrzení)
                                        </span>
                                    </>
                                ) : (
                                    <button
                                        className={`job_item_action_btn ${isPositionBooked ? "disabled" : ""}`}
                                        onClick={onInterestClick}
                                        disabled={isPositionBooked}
                                    >
                                        {isPositionBooked ? "Obsazeno" : "Mám zájem"}
                                    </button>
                                )}
                            </>
                        )}
                        {!isOwner && positionId && (
                            <FontAwesomeIcon
                                icon={isPosFav ? faHeartFull : faHeartEmpty}
                                className={`job_item_heart ${isPosFav ? "fav-active" : ""}`}
                                onClick={handlePositionFavClick}
                            />
                        )}
                        {onShareClick !== undefined && (
                            <FontAwesomeIcon
                                icon={faCopy}
                                className="job_item_share"
                                onClick={onShareClick}
                            />
                        )}
                    </aside>
                ) : (
                    <aside className="job_item_organizer_panel">
                        <img
                            className="job_item_organizer_avatar"
                            src={organizerImage || ASSETS.default_avatar}
                            alt={organizerName || "Organizátor"}
                            loading="lazy"
                        />
                        <div className="job_item_organizer_info">
                            {organizerRating && organizerRating > 0 && (
                                <div className="job_item_panel_rating">
                                    <Stars rating={organizerRating} />
                                    <span className="rating_num">{organizerRating}</span>
                                </div>
                            )}

                            {organizerName && (
                                <div className="job_item_organizer_header">
                                    <span className="job_item_organizer_name">{organizerName}</span>
                                    {organizerVerified && <CertificateIcon className="certificate_icon--small" />}
                                </div>
                            )}
                        </div>
                    </aside>
                )}

                {/* Tabulka přihlášených pracovníků pro majitele */}
                {isOwner && isDetailMode && (
                    <div className="job_item_applicants_section">
                        <h4 className="job_item_applicants_header">Uchazeči</h4>
                        {applicants && applicants.length > 0 ? (
                            <div className="job_item_applicants_table_container">
                                <table className="job_item_applicants_table">
                                    <thead>
                                        <tr>
                                            <th>Jméno</th>
                                            <th>Příjmení</th>
                                            <th>Hodnocení</th>
                                            <th>Uložili si</th>
                                            <th>Stav</th>
                                            <th>Akce</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {applicants.map((app) => (
                                            <tr key={app.application_id}>
                                                <td className="applicant_first_name">
                                                    {app.first_name}
                                                </td>
                                                <td className="applicant_last_name">
                                                    {app.last_name}
                                                </td>
                                                <td className="applicant_rating">
                                                    {app.user_rating > 0
                                                        ? <><Stars rating={app.user_rating} /> {app.user_rating}</>
                                                        : "---"}
                                                </td>
                                                <td className="applicant_saved">
                                                    <FontAwesomeIcon
                                                        icon={getApplicantFavState(app) ? faHeartFull : faHeartEmpty}
                                                        className={`job_item_heart ${getApplicantFavState(app) ? "fav-active" : ""}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleApplicantFavClick(app.user_id);
                                                        }}
                                                    />
                                                </td>
                                                <td className="applicant_status">
                                                    <span className={`applicant_status_badge badge_${app.status}`}>
                                                        {app.status === "pending"
                                                            ? "Čekající"
                                                            : app.status === "confirmed" || app.status === "accepted"
                                                            ? "Potvrzeno"
                                                            : app.status === "rejected"
                                                            ? "Odmítnuto"
                                                            : app.status === "withdrawn"
                                                            ? "Zrušeno"
                                                            : app.status}
                                                    </span>
                                                </td>
                                                <td className="applicant_actions">
                                                    {app.status === "pending" && (
                                                        <>
                                                            <button
                                                                className="applicant_action_btn approve"
                                                                onClick={() =>
                                                                    onApplicationAction(app.application_id, "confirmed")
                                                                }
                                                            >
                                                                Potvrdit
                                                            </button>
                                                            <button
                                                                className="applicant_action_btn reject"
                                                                onClick={() =>
                                                                    onApplicationAction(app.application_id, "rejected")
                                                                }
                                                            >
                                                                Odmítnout
                                                            </button>
                                                        </>
                                                    )}
                                                    {(app.status === "confirmed" || app.status === "accepted") && (
                                                        <button
                                                            className="applicant_action_btn cancel"
                                                            onClick={() =>
                                                                onApplicationAction(app.application_id, "withdrawn")
                                                            }
                                                        >
                                                            Zrušit směnu
                                                        </button>
                                                    )}
                                                    {app.status === "rejected" && (
                                                        <button
                                                            className="applicant_action_btn restore"
                                                            onClick={() =>
                                                                onApplicationAction(app.application_id, "pending")
                                                            }
                                                        >
                                                            Obnovit
                                                        </button>
                                                    )}
                                                    <button
                                                        className="applicant_action_btn chat"
                                                        onClick={() => onOpenChatWithApplicant(app.user_id)}
                                                    >
                                                        Chat
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="job_item_applicants_empty">
                                Žádní zájemci
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
});

export default memo(JobItemBase);
